import { useMemo } from 'react';
import {
  Box,
  Card,
  Stack,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  CardHeader,
  Typography,
  CardContent,
  TableContainer,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IOrderDocument } from '@/types/order-document';
import Iconify from '@/components/media/iconify';
import Scrollbar from '@/components/layout/scrollbar';
import { OrderDocumentTableRow } from '../documents/order-document-table-row';
import { OrderDocumentDetailTableRow } from '../documents/order-document-detail-table-row';

interface OrderDocumentsSectionProps {
  documents: IOrderDocument[];
  onAddDocument?: () => void;
  onEditDocument?: (document: IOrderDocument) => void;
  onDeleteDocument?: (document: IOrderDocument) => void;
  title?: string;
  subtitle?: string;
  NormalView?: boolean;
  sectionsToHide?: {
    AddButton?: boolean;
    EmptyState?: boolean;
    DocumentTable?: boolean;
    EditButton?: boolean;
    DeleteButton?: boolean;
  };
}

export function OrderDocumentsSection({
  documents = [],
  onEditDocument,
  onDeleteDocument,
  title,
  subtitle,
  sectionsToHide = {},
  NormalView = true,
}: OrderDocumentsSectionProps) {
  const { t } = useTranslation();

  const displayTitle = useMemo(() => {
    if (title) return title;
    return t('Attached Documents');
  }, [title, t]);

  const displaySubtitle = useMemo(() => {
    if (subtitle) return subtitle;
    if (documents.length === 0) {
      return t('No documents attached yet');
    }
    return `${documents.length} ${documents.length === 1 ? t('document attached') : t('documents attached')}`;
  }, [subtitle, documents.length, t]);

  if (documents.length === 0) {
    if (sectionsToHide.EmptyState) {
      return null;
    }

    return (
      <Card sx={{ mb: 2 }}>
        {!sectionsToHide.DocumentTable && (
          <CardContent>
            <Stack spacing={2}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {displayTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {displaySubtitle}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                  bgcolor: 'background.neutral',
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                <Stack spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'background.paper',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                    }}
                  >
                    <Iconify
                      icon="solar:document-add-bold"
                      sx={{ color: 'text.disabled', width: 24, height: 24 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('Use the form above to add your first document')}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        )}
      </Card>
    );
  }
  if (NormalView) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader title={displayTitle} subheader={displaySubtitle} />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <TableContainer>
            <Scrollbar>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Type')}</TableCell>
                    <TableCell>{t('File Name')}</TableCell>
                    <TableCell>{t('Date')}</TableCell>
                    <TableCell align="center">{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((document, index) => (
                    <OrderDocumentTableRow
                      key={document._id || index}
                      row={document}
                      onEditRow={
                        onEditDocument && !sectionsToHide.EditButton
                          ? () => onEditDocument(document)
                          : () => {}
                      }
                      onDeleteRow={onDeleteDocument ? () => onDeleteDocument(document) : () => {}}
                      hideEditButton={sectionsToHide.EditButton || false}
                      hideDeleteButton={sectionsToHide.DeleteButton || false}
                    />
                  ))}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader title={displayTitle} subheader={displaySubtitle} />
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <TableContainer>
          <Scrollbar>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('Document Type')}</TableCell>
                  <TableCell>{t('File Name')}</TableCell>
                  <TableCell>{t('Submitted Date')}</TableCell>
                  <TableCell>{t('Submitted By')}</TableCell>
                  <TableCell>{t('OC Metadata')}</TableCell>
                  <TableCell align="center">{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document, index) => (
                  <OrderDocumentDetailTableRow key={document._id || index} row={document} />
                ))}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
